import axios, { type AxiosError, HttpStatusCode, type InternalAxiosRequestConfig } from 'axios';

import {
	type HttpInterceptor,
	type HttpInterceptorCallback,
	type HttpUnauthorizedHandler,
	isNil
} from '@contexts/shared/domain';

type RetriableRequestConfig = InternalAxiosRequestConfig & {
	hasBeenRetriedAfterRefresh?: boolean;
};

export function axiosHttpInterceptorBuilder(): HttpInterceptor {
	return {
		interceptResponse: (callback: HttpInterceptorCallback): void => {
			axios.interceptors.response.use(
				(response) => {
					callback(response.status, response.config.url || '');
					return response;
				},
				(error) => {
					callback(error.response.status, error.response.config.url || '');
					return Promise.reject(error);
				}
			);
		},
		interceptUnauthorized: (handler: HttpUnauthorizedHandler): void => {
			axios.interceptors.response.use(
				(response) => response,
				async (error: AxiosError) => {
					const config = error.config as RetriableRequestConfig | undefined;

					if (
						error.response?.status !== HttpStatusCode.Unauthorized ||
						isNil(config) ||
						config.hasBeenRetriedAfterRefresh === true
					) {
						return Promise.reject(error);
					}

					config.hasBeenRetriedAfterRefresh = true;

					const isSessionRenewed = await handler(config.url || '');
					if (!isSessionRenewed) {
						return Promise.reject(error);
					}

					return axios.request(config);
				}
			);
		}
	};
}
